import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const HOME = { admin: '/admin/dashboard', master_admin: '/admin/dashboard', cleaner: '/cleaner/dashboard', client: '/portal/dashboard' };

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [invite, setInvite] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await login(email, password);
      navigate(HOME[user.role] || '/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  return (
    <main className="gg-ws min-h-[100svh] grid grid-cols-1 md:grid-cols-2 bg-[#f2f1eb] text-[#23251f]" data-testid="login-page">
      <section className="relative overflow-hidden bg-[#171a13] text-[#f5f3ea] px-7 py-8 md:px-[68px] md:py-14 flex flex-col justify-between">
        <span aria-hidden className="pointer-events-none absolute w-[500px] h-[500px] rounded-full border border-[#353b2c] -right-[240px] -bottom-[140px]" />
        <Link to="/" aria-label="Gleam Guru home"><img src="/email-assets/gleam-guru-logo.png" alt="Gleam Guru Cleaners" className="w-[132px] md:w-[176px] h-auto object-contain" /></Link>
        <div className="py-10 md:py-[90px]">
          <p className="text-[11px] tracking-[2px] font-bold text-[#aeb69a]">THE GLEAM GURU WORKSPACE</p>
          <h1 className="text-[46px] md:text-[clamp(46px,5.4vw,84px)] leading-[1.03] tracking-[-0.05em] font-bold my-6">Everything.<br />Right here.</h1>
          <p className="text-lg text-[#bec3b4]">Your people. Your space. Your next step.</p>
        </div>
        <div className="hidden md:flex justify-between gap-5 text-[10px] tracking-[1px] text-[#a3aa94]"><span>CLIENTS · GURUS · OPERATIONS</span><span>Gleam Guru Cleaners</span></div>
      </section>

      <section className="px-7 py-9 md:px-[clamp(28px,6vw,100px)] flex flex-col justify-center">
        <h2 className="text-4xl font-bold tracking-[-1px] mb-3.5">Welcome back.</h2>
        <p className="text-[#64695c] leading-relaxed" data-testid="login-tagline">Sign in to your <span className="text-[#f36a19] font-bold text-[115%]">Gleam Guru</span> workspace.</p>
        <form onSubmit={handleSubmit} noValidate={false}>
          {error && <div role="alert" className="mt-6 bg-red-50 text-red-700 p-3 rounded-lg flex items-center gap-2 text-sm" data-testid="login-error"><AlertCircle className="h-4 w-4" />{error}</div>}
          <label htmlFor="email" className="block text-[13px] font-bold mt-6 mb-2">Email address</label>
          <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="w-full border border-[#cbd0bf] bg-[#fcfcf7] px-4 py-3.5 rounded-[9px] min-h-[49px] focus:outline-none focus:ring-2 focus:ring-[#f36a19]" data-testid="login-email" />
          <label htmlFor="password" className="block text-[13px] font-bold mt-6 mb-2">Password</label>
          <div className="relative">
            <input id="password" type={show ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
              className="w-full border border-[#cbd0bf] bg-[#fcfcf7] pl-4 pr-20 py-3.5 rounded-[9px] min-h-[49px] focus:outline-none focus:ring-2 focus:ring-[#f36a19]" data-testid="login-password" />
            <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-[7px] px-2.5 py-2.5 text-[#48513d] text-sm" aria-label={show ? 'Hide password' : 'Show password'} data-testid="login-toggle-password">{show ? 'Hide' : 'Show'}</button>
          </div>
          <button type="submit" disabled={loading} className="mt-6 w-full bg-[#252d1f] hover:bg-black text-white rounded-[9px] py-4 font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-70" data-testid="login-submit">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : <>Sign in <ArrowUpRight className="h-4 w-4" /></>}
          </button>
        </form>
        <div className="flex justify-between gap-4 mt-6 text-sm">
          <Link to="/forgot-password" className="underline text-[#48513d] hover:text-[#f36a19]" data-testid="login-forgot-link">Forgot password?</Link>
          <button type="button" onClick={() => setInvite((v) => !v)} className="underline text-[#48513d] hover:text-[#f36a19]" aria-expanded={invite} data-testid="login-invite-toggle">Have an invitation?</button>
        </div>
        {invite && <p className="mt-3 text-sm text-[#64695c] bg-[#e9eadf] rounded-lg p-3" data-testid="login-invite-help">Open the secure link in your invitation email to set your password. Links expire for your security. If yours has expired, ask the Gleam Guru team to send a new one.</p>}
        <div className="flex justify-between mt-12 text-[11px] text-[#6f775f]">
          <Link to="/" className="underline" data-testid="login-back-to-site">Back to website ↗</Link><span>Gleam Guru Cleaners</span>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
